import React from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  CheckCircle,
  Sparkles
} from 'lucide-react'

const MomentsGalleryPage: React.FC = () => {
  const navigate = useNavigate()
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }

  const cardVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F9F9' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-8 pb-12"
        >
          <Link
            to="/moment-perks"
            className="inline-flex items-center space-x-2 hover:opacity-80 transition-opacity duration-300 mb-8"
            style={{ color: '#6E6D7A' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Back to Moment Perks</span>
          </Link>

          <div className="text-center">
            <motion.h1
              variants={itemVariants}
              className="text-5xl font-bold mb-6"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                color: '#1A2B49'
              }}
            >
              Moments Gallery
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-lg max-w-3xl mx-auto"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                color: '#6E6D7A'
              }}
            >
              Explore ready-made moment templates to enhance your customer experience
            </motion.p>
          </div>
        </motion.div>

        {/* Gallery Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="pb-16"
        >
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Order Thank You Page Card */}
            <motion.div
              variants={cardVariants}
              onClick={() => navigate('/moments-example-order-thank-you')}
              className="group cursor-pointer bg-white rounded-xl transition-all duration-300 hover:shadow-lg"
              style={{ 
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}
            >
              {/* Card Content */}
              <div className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: '#FE4215' }}
                  >
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#1A2B49'
                      }}
                    >
                      Order Thank You Page
                    </h3>
                    <p 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Complete order confirmation experience with customer reassurance and next steps
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Order summary & tracking
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Exclusive offers integration
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Professional design & UX
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  className="w-full py-4 px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90"
                  style={{ 
                    backgroundColor: '#FE4215',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px'
                  }}
                >
                  Preview Template
                </button>
              </div>
            </motion.div>

            {/* Customer Support Demo Card */}
            <motion.div
              variants={cardVariants}
              onClick={() => navigate('/customer-support')}
              className="group cursor-pointer bg-white rounded-xl transition-all duration-300 hover:shadow-lg"
              style={{ 
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}
            >
              {/* Card Content */}
              <div className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: '#2563EB' }}
                  >
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#1A2B49'
                      }}
                    >
                      Customer Support Demo
                    </h3>
                    <p 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Experience personalized offers while waiting for customer support using real-time API integration
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Live offer fetching from API
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Real-time wait queue simulation
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5" style={{ color: '#FE4215' }} />
                    <span 
                      className="text-base"
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#6E6D7A'
                      }}
                    >
                      Interactive offer selection
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  className="w-full py-4 px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90"
                  style={{ 
                    backgroundColor: '#FE4215',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px'
                  }}
                >
                  Try Support Demo
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default MomentsGalleryPage
